import classNames from 'classnames/bind';
import styles from './InformationItem.module.css';
import { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { useSlice } from '../../../hooks/useSlice';
import { selectMenu } from '../../../features/infoSlice';

import {
  formatBytes,
  copyToClipboard,
  VITE_URL,
  wrapUrlsWithCustomLink,
} from '../../../utils/util';
import { formatDate, getDiff } from '../../../utils/date';
import { IndexItem, LibraryKey } from '../../../types/library';
import { UseInfo } from '../../../types/drive';

import Input from '../../Input';
import {
  EBOOK_INFO_LIST,
  PLACEHOLDER_MEMO,
  FILE_INFO_LIST,
  FILE_NOT_SHARED,
  COPY,
  SHARED_LINK,
  SHARED_WITH_USER,
  PUBLIC_FILE,
  NOT_PUBLIC_FILE,
  NUMBER_ABBR,
  DATE,
  TIME,
  PAGE,
  SHARED_USER,
  RESOURCE_INFO_LIST,
  OWNER,
  SHARED_PERIOD,
  HITS,
  PUBLIC_DATE,
  KEYWORD,
  NO_MEMO,
  MORE,
  NO_INDEX,
  NO_LIMITATION,
  END_SHARING,
  FOLDER_USAGE_SUMMARY,
  FOLDER_INFO_LIST,
  FILE_USAGE_SUMMARY,
  DRIVE,
  COPYRIGHT_INFO_LIST,
} from '../../../constants';
import Button from '../../Button';
import { isOfType } from '../../../utils/typeGuard';

interface Item {
  dataKey: LibraryKey;
}

interface History {
  date: string;
  lastPage: number;
  projectId: number;
  regDate: string;
  seq: number;
  time: string;
  uptDate: string;
  userId: number;
}

export default function InformationItem({ dataKey }: Item) {
  const cx = classNames.bind(styles);
  const menu = useAppSelector(selectMenu);
  const dispatch = useAppDispatch();
  const { selectSingleItem, selectSelectedItem, itemSelected } = useSlice();
  const singleItem = useAppSelector(selectSingleItem);
  const selectedItem = useAppSelector(selectSelectedItem);
  const [isShowAll, setIsShowAll] = useState(false);

  const dataVal = selectedItem && selectedItem[dataKey];
  const isFolder = selectedItem?.resource?.ext === 'folder';

  const showAllList = () => setIsShowAll(!isShowAll);

  if (
    !dataVal &&
    !dataKey.toLowerCase().includes('note') &&
    dataKey !== 'share' &&
    dataKey !== 'url'
  )
    return null;

  useEffect(() => {
    singleItem &&
      dispatch(
        itemSelected({
          ids: isOfType(singleItem, 'project')
            ? [singleItem.project.projectId]
            : isOfType(singleItem.resource, 'resourceId')
              ? [singleItem.resource.resourceId]
              : [],
          item: singleItem,
        })
      );
  }, [singleItem]);

  switch (dataKey) {
    case 'project':
      return (
        <>
          {EBOOK_INFO_LIST.map(([title, key], idx) =>
            key !== 'orgCoverYn' ? (
              <div key={key} className={cx('panel_row')}>
                {<span className={cx('sub')}>{title}</span>}
                {idx ? (
                  <span className={cx('detail')}>
                    {key === 'pubDate'
                      ? dataVal[key]
                        ? formatDate(dataVal[key])
                        : formatDate(
                            isOfType(selectedItem, 'projectResource')
                              ? selectedItem.projectResource?.regDate
                              : ''
                          )
                      : dataVal[key]}
                  </span>
                ) : (
                  <em className={cx('detail')}>{dataVal[key]}</em>
                )}
              </div>
            ) : null
          )}
        </>
      );
    case 'projectCopyright':
      return (
        <>
          {COPYRIGHT_INFO_LIST.map(([title, key]) => {
            return (
              <div key={key} className={cx('panel_row')}>
                <span className={cx('sub')}>{title}</span>

                <span className={cx('detail')}>
                  {wrapUrlsWithCustomLink(dataVal[key], ['skyt'])}
                </span>
              </div>
            );
          })}
        </>
      );
    case 'projectIndex':
      const list = dataVal.indexList;
      const listLen =
        list.length === 1 && list[0].content === '' ? 0 : list.length;

      return (
        <div className={cx('panel_row')}>
          {!listLen ? (
            <span className={cx('detail')}>
              <span className={cx('not')}>{NO_INDEX}</span>
            </span>
          ) : (
            <>
              <div className={cx('unit_items', { active: isShowAll })}>
                {list.map(
                  ({ indentYn, content }: IndexItem, i: number) =>
                    (isShowAll || (!isShowAll && i <= 2)) && (
                      <p key={`${content}_${i}`} className={cx('items')}>
                        <span className={cx(`depth${indentYn + 1}`)}>
                          {content}
                        </span>
                      </p>
                    )
                )}
              </div>
              {listLen > 3 && !isShowAll && (
                <Button
                  name={MORE}
                  classnames={['open_list_more']}
                  isBlindName={true}
                  handleClick={showAllList}
                >
                  <i className={cx('svg_i')}></i>
                </Button>
              )}
            </>
          )}
        </div>
      );
    case 'projectNote':
    case 'note':
      const isDisabled = menu === 'trash';
      return (
        <div className={cx('panel_row')}>
          <span className={cx('detail')}>
            <Input
              type="text"
              id={dataKey}
              classnames={['input_t', 'note']}
              value={isDisabled && !dataVal ? NO_MEMO : dataVal}
              placeholder={PLACEHOLDER_MEMO}
              isDisabled={isDisabled}
            />
          </span>
        </div>
      );
    case 'projectResource':
      return (
        <>
          {FILE_INFO_LIST.map(
            ([title, key]) =>
              !(
                (key === 'selpubSize' && !dataVal['selpubSize']) ||
                (key === 'shareDate' && menu !== 'share') ||
                (key === 'uptDate' && !dataVal['uptDate'])
              ) && (
                <div key={key} className={cx('panel_row')}>
                  <span className={cx('sub')}>{title}</span>
                  <span className={cx('detail')}>
                    {key === 'orgFileName'
                      ? `${dataVal[key]} (${formatBytes(dataVal.size)})`
                      : key.includes('Size')
                        ? formatBytes(dataVal[key])
                        : key === 'shareDate'
                          ? formatDate(
                              isOfType(selectedItem, 'projectShare')
                                ? selectedItem.projectShare.shareDate
                                : ''
                            )
                          : key.includes('Date')
                            ? formatDate(dataVal[key])
                            : dataVal[key]}
                  </span>
                </div>
              )
          )}
        </>
      );
    case 'resource':
      return (
        <>
          {isFolder
            ? FOLDER_INFO_LIST.map(([title, key]) => (
                <div key={key} className={cx('panel_row')}>
                  <span className={cx('sub')}>{title}</span>
                  {key === 'name' ? (
                    <em className={cx('detail')}>{dataVal[key]}</em>
                  ) : (
                    <span className={cx('detail')}>
                      {key.includes('Date')
                        ? formatDate(dataVal[key])
                        : dataVal[key]}
                    </span>
                  )}
                </div>
              ))
            : RESOURCE_INFO_LIST.map(([title, key]) => {
                return (menu === 'trash' &&
                  (dataVal.location === DRIVE
                    ? key === 'title'
                    : key === 'orgFileName')) ||
                  !dataVal[key] ? null : (
                  <div key={key} className={cx('panel_row')}>
                    <span className={cx('sub')}>{title}</span>
                    <span className={cx('detail')}>
                      {key === 'selpubSize'
                        ? formatBytes(dataVal[key])
                        : key === 'ext'
                          ? dataVal['ext'] === 'web'
                            ? dataVal['ext']
                            : `${dataVal['orgFileName']} (${formatBytes(
                                dataVal['size']
                              )})`
                          : key.includes('Date')
                            ? formatDate(dataVal[key])
                            : dataVal[key]}
                    </span>
                  </div>
                );
              })}
        </>
      );
    case 'projectShare':
    case 'share':
      const hasLink =
        typeof dataVal?.shareLink !== 'undefined'
          ? !!dataVal?.shareLink?.link
          : !!dataVal?.link;
      const sharedUserLen = dataVal?.shareUser?.emailList.length;
      const linkUrl = `${VITE_URL}/share?link=${
        dataVal?.shareLink?.link || dataVal?.link
      }&resourceYn=${+(menu === 'mydrive')}`;
      const hasExpDate = !!dataVal?.expDate;
      const dateDiff = getDiff(dataVal?.expDate);
      const isSharingEnd = dateDiff < 0 || !!dataVal?.expYn;

      return (
        <>
          {menu === 'share' && (
            <>
              <div className={cx('panel_row')}>
                <span className={cx('sub')}>{OWNER}</span>
                <span className={cx('detail')}>{dataVal.owner}</span>
              </div>

              <div className={cx('panel_row')}>
                <span className={cx('sub')}>{SHARED_PERIOD}</span>
                <span className={cx('detail')}>
                  <span>
                    {formatDate(dataVal.shareDate)} ~{' '}
                    {hasExpDate ? formatDate(dataVal.expDate) : NO_LIMITATION}
                    <em
                      className={cx('dday', {
                        expiration: isSharingEnd,
                      })}
                    >
                      {hasExpDate &&
                        `(D-${isSharingEnd ? 0 : Math.abs(dateDiff)})`}
                    </em>
                  </span>
                  {isSharingEnd && (
                    <span className={cx('msg')}>{END_SHARING}</span>
                  )}
                </span>
              </div>
            </>
          )}
          {!hasLink && sharedUserLen ? null : (
            <div className={cx('panel_row', 'a_center')}>
              {hasLink && <span className={cx('sub')}>{SHARED_LINK}</span>}
              <span className={cx('detail')}>
                {hasLink ? (
                  <div className={cx('share_url')}>
                    <span className={cx('url', 'text_clip')}>{linkUrl}</span>
                    <span
                      role="button"
                      className={cx('btn', 'copyurl')}
                      onClick={() => copyToClipboard(linkUrl)}
                    >
                      {COPY}
                    </span>
                  </div>
                ) : (
                  menu !== 'share' && (
                    <span className={cx('not')}>{FILE_NOT_SHARED}</span>
                  )
                )}
              </span>
            </div>
          )}

          {sharedUserLen ? (
            <div className={cx('panel_row')}>
              <span className={cx('sub')}>{SHARED_USER}</span>
              <span className={cx('detail')}>
                <span>
                  {SHARED_WITH_USER.replace('{N}', sharedUserLen.toString())}
                </span>
              </span>
            </div>
          ) : null}
        </>
      );
    case 'projectPublic':
      const publicInfoList = [HITS, OWNER, PUBLIC_DATE, KEYWORD];
      const keys = ['hit', 'owner', 'publicDate', 'publicKeyword'];
      const keywords = ['publicKeyword1', 'publicKeyword2', 'publicKeyword3'];
      const keywordLen = keywords.filter((key) => dataVal[key] !== '').length;

      return menu !== 'search' ? (
        <div className={cx('panel_row')}>
          <span className={cx('detail')}>
            <span className={cx({ not: !dataVal.publicYn })}>
              {dataVal.publicYn ? PUBLIC_FILE : NOT_PUBLIC_FILE}
            </span>
          </span>
        </div>
      ) : (
        <>
          {publicInfoList.map((info, i) =>
            info !== KEYWORD || keywordLen ? (
              <div key={info} className={cx('panel_row')}>
                <span className={cx('sub')}>{info}</span>
                <span className={cx('detail')}>
                  {info === KEYWORD ? (
                    <>
                      {keywords.map(
                        (keyword) =>
                          dataVal[keyword] && (
                            <span key={keyword} className={cx('word')}>
                              #{dataVal[keyword]}
                            </span>
                          )
                      )}
                    </>
                  ) : keys[i].includes('Date') ? (
                    formatDate(dataVal[keys[i]])
                  ) : (
                    dataVal[keys[i]]
                  )}
                </span>
              </div>
            ) : null
          )}
        </>
      );
    case 'projectHistoryList':
      return (
        <div className={cx('panel_row', 'column')}>
          <div className={cx('table_list', 'table_head', 'activity_head')}>
            <span>{NUMBER_ABBR}</span>
            <span>{DATE}</span>
            <span>{TIME}</span>
            <span>{PAGE}</span>
          </div>

          <div className={cx('table_list', 'table_body', 'activity_body')}>
            {dataVal.map(
              ({ seq, date, time, lastPage }: History, idx: number) =>
                idx > 2 ? null : (
                  <span key={seq} className={cx('items')}>
                    <span>{seq}</span>
                    <span>{formatDate(date)}</span>
                    <span>{time}</span>
                    <span>{lastPage}</span>
                  </span>
                )
            )}
            {dataVal.length > 3 && <span className={cx('more')}></span>}
          </div>
        </div>
      );
    case 'url':
      return (
        <div className={cx('panel_row')}>
          <span className={cx('sub')}>URL</span>
          <span className={cx('detail')}>{selectedItem?.resource.url}</span>
        </div>
      );

    case 'useList':
      const useListLen = dataVal?.length;
      return (
        <div className={cx('panel_row', 'column')}>
          <div
            className={cx('detail', 'use_summary')}
            dangerouslySetInnerHTML={{
              __html: (isFolder
                ? FOLDER_USAGE_SUMMARY
                : FILE_USAGE_SUMMARY
              ).replace('{N}', useListLen.toString()),
            }}
          ></div>
          <div className={cx('unit_items', { active: isShowAll })}>
            {dataVal.map(({ pageList, title }: UseInfo, i: number) => (
              <p key={`${i}_${title}`} className={cx('items')}>
                <span className={cx('detail')}>{title}</span>
                <span className={cx('page')}>{`${pageList.join('p, ')}p`}</span>
              </p>
            ))}
          </div>
          {useListLen > 3 ? (
            <Button
              name={MORE}
              classnames={['open_list_more', isShowAll ? 'on' : '']}
              isBlindName={true}
              handleClick={showAllList}
            >
              <i className={cx('svg_i')}></i>
            </Button>
          ) : null}
        </div>
      );
    default:
      return null;
  }
}
